import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { isNativeApp } from '../../util/nativeApp';

import css from './AppPromoBanner.module.css';

// Off unless explicitly switched on, so the banner can be merged and deployed
// long before the app is actually live.
// e.g. REACT_APP_APP_PROMO_BANNER=on
//
// NB: env vars must be read via static `process.env.REACT_APP_*` access - the
// build only inlines that literal form, not dynamic `process.env[key]` lookups.
// They're read per render rather than at module load so tests can vary them
// without re-importing the module (which would pull in a second copy of React).
const isOn = value => ['on', 'true', '1', 'yes'].includes(String(value).trim().toLowerCase());

const getConfig = () => ({
  isEnabled: isOn(process.env.REACT_APP_APP_PROMO_BANNER),
  appStoreUrl: process.env.REACT_APP_APP_STORE_URL,
  playStoreUrl: process.env.REACT_APP_PLAY_STORE_URL,
});

// Bump the suffix to show the banner again to people who dismissed an earlier
// version of it.
const DISMISSED_KEY = 'shimmy_app_promo_dismissed_v1';

// The strip sits in the page flow, so it's shown as soon as possible to keep
// the layout from jumping. Twinr's JS bridge globals can land a moment after
// the first paint though, so re-check once shortly after and pull the strip if
// it turns out we're inside the app after all. Setting
// REACT_APP_NATIVE_APP_UA_TOKEN avoids that second pass entirely, since the
// user agent is right there on the first check.
const RECHECK_DELAY_MS = 1200;

const isBrowser = typeof window !== 'undefined';

const wasDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === 'true';
  } catch (e) {
    // localStorage is unavailable (private mode, blocked cookies) - show the
    // banner rather than hiding it, the user can still close it.
    return false;
  }
};

const rememberDismissal = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, 'true');
  } catch (e) {
    // Nothing to do - the dismissal just won't survive a reload.
  }
};

const getPlatform = () => {
  const ua = window.navigator?.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'ios';
  }
  if (/Android/i.test(ua)) {
    return 'android';
  }
  return 'other';
};

// Store marks, drawn inline so they stay crisp and inherit the button's colour.
// Decorative only - the adjacent text label is what screen readers announce.
const AppleGlyph = () => (
  <svg className={css.storeIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
    />
  </svg>
);

const PlayGlyph = () => (
  <svg className={css.storeIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.13 12l2.567-2.49zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"
    />
  </svg>
);

/**
 * A dismissible strip promoting the native mobile app, sitting in the page flow
 * directly under the landing page's genre row.
 *
 * Hidden unless REACT_APP_APP_PROMO_BANNER is switched on, and never shown to
 * people who are already inside the app (see util/nativeApp).
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.intl
 * @returns {JSX.Element|null} AppPromoBanner element, or null when it shouldn't be shown
 */
const AppPromoBannerComponent = props => {
  const { className, rootClassName, intl } = props;
  const { isEnabled, appStoreUrl, playStoreUrl } = getConfig();
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState('other');

  useEffect(() => {
    if (!isEnabled || !isBrowser || wasDismissed() || isNativeApp()) {
      return undefined;
    }

    setPlatform(getPlatform());
    setIsVisible(true);

    const timeoutId = window.setTimeout(() => {
      if (isNativeApp()) {
        setIsVisible(false);
      }
    }, RECHECK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isEnabled]);

  const handleClose = () => {
    setIsVisible(false);
    rememberDismissal();
  };

  // Both store links missing means there's nothing to send anyone to.
  if (!isVisible || (!appStoreUrl && !playStoreUrl)) {
    return null;
  }

  const storeLink = (key, url, glyph, labelId) =>
    url ? (
      <a
        key={key}
        className={css.storeButton}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {glyph}
        <FormattedMessage id={labelId} />
      </a>
    ) : null;

  const appStoreLink = storeLink('ios', appStoreUrl, <AppleGlyph />, 'AppPromoBanner.appStore');
  const playStoreLink = storeLink(
    'android',
    playStoreUrl,
    <PlayGlyph />,
    'AppPromoBanner.playStore'
  );

  // Lead with the store the visitor can actually install from.
  const storeLinks =
    platform === 'android' ? [playStoreLink, appStoreLink] : [appStoreLink, playStoreLink];

  return (
    <div className={classNames(rootClassName || css.root, className)} role="complementary">
      <div className={css.inner}>
        <p className={css.message}>
          <FormattedMessage id="AppPromoBanner.message" />
        </p>

        <div className={css.stores}>{storeLinks}</div>
      </div>

      <button
        className={css.closeButton}
        onClick={handleClose}
        title={intl.formatMessage({ id: 'AppPromoBanner.closeLabel' })}
        aria-label={intl.formatMessage({ id: 'AppPromoBanner.closeLabel' })}
        type="button"
      >
        <svg viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path
            d="M1 1l10 10M11 1L1 11"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default injectIntl(AppPromoBannerComponent);
